import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { area as turfArea, distance as turfDistance, polygon as turfPolygon } from '@turf/turf';
import { GeoJSON } from 'geojson';
import { AddLayerObject, GeoJSONSource, GeoJSONSourceSpecification, LngLatLike, MapMouseEvent } from 'maplibre-gl';

import { MapComponent } from '@connect-services/client/components/map/map.component';
import { AssertService } from '@connect-services/client/services/assert.service';
import { TileSourceTypeEnum } from '@connect-services/client/services/basemap.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { Feature, FeatureCollection, Geometry, Point } from '@connect-services/src/sdk/api';

enum AreaUnit {
    SQUARE_METER = 'm²',
    HECTARE = 'ha',
}

enum DistanceUnit {
    KILOMETER = 'km',
    METER = 'm',
}

class Area {
    public constructor(
        private readonly unit: AreaUnit,
        private readonly value: number,
    ) {}

    public toString(): string {
        return `${this.value.toFixed(2)} ${this.unit}`;
    }
}

class Distance {
    public constructor(
        private readonly unit: DistanceUnit,
        private readonly value: number,
    ) {}

    public toString(decimals: number = 2): string {
        return `${this.value.toFixed(decimals)} ${this.unit}`;
    }
}

@Injectable({
    providedIn: 'root',
})
export class MeasurementToolService extends MapBaseService {
    public static measurementSourceId = 'measurement-source';

    private static readonly oneHectare = 10000;
    private static readonly oneKilometer = 1000;

    public readonly area: WritableSignal<Area> = signal(new Area(AreaUnit.SQUARE_METER, 0));
    public readonly canDraw: WritableSignal<boolean> = signal(false);
    public readonly canUndo: WritableSignal<boolean> = signal(false);
    public readonly distance: WritableSignal<Distance> = signal(new Distance(DistanceUnit.METER, 0));
    public readonly hasArea: WritableSignal<boolean> = signal(false);

    public readonly hasDistance: WritableSignal<boolean> = signal(false);
    public readonly isActive: WritableSignal<boolean> = signal(false);

    public readonly measurementPhantomSourceId: string = 'measurement-phantom-source';
    public readonly measurementSourceId: string = 'measurement-source';

    private addNewPointFunctionInstance?: (mapClickEvent: MapMouseEvent) => void;
    private drawPhantomLineFunctionInstance?: (mapClickEvent: MapMouseEvent) => void;
    private readonly layerNames: string[] = [
        'measurement-lines',
        'measurement-polygon',
        'measurement-points',
        'measurement-phantom-lines',
    ];
    private readonly mapSourceService = inject(MapSourceService);

    public calculateArea(points: Point[], isPolygonClosed: boolean): Area {
        if (!isPolygonClosed) {
            return new Area(AreaUnit.SQUARE_METER, 0);
        }

        const polygon = turfPolygon([points.map((point) => point.coordinates)]);
        const areaInSquareMeters = turfArea(polygon);

        if (areaInSquareMeters > MeasurementToolService.oneHectare) {
            const areaInHectares = areaInSquareMeters / MeasurementToolService.oneHectare;

            return new Area(AreaUnit.HECTARE, areaInHectares);
        }

        return new Area(AreaUnit.SQUARE_METER, areaInSquareMeters);
    }

    public async calculateDistance(isPolygonClosed: boolean = false): Promise<void> {
        if (!this.map) {
            return;
        }

        const source = this.map.getSource(this.measurementSourceId) as GeoJSONSource | undefined;
        if (!source) {
            return;
        }

        const data = (await source.getData()) as FeatureCollection;
        if (!data) {
            return;
        }

        const features = data.features.filter((feature: Feature) => feature.geometry.type === Point.TypeEnum.POINT);

        const points: Point[] = features.map((feature: Feature) => {
            AssertService.assertPointType(feature.geometry);

            return feature.geometry;
        });

        if (isPolygonClosed) {
            points.push(points[0]);
        }

        const distance = this.calculateLinesDistance(points);
        const area = this.calculateArea(points, isPolygonClosed);

        this.distance.set(distance);
        this.area.set(area);

        this.hasDistance.set(points.length > 1);
        this.hasArea.set(isPolygonClosed);

        await this.updateUndoState();
    }

    public calculateLinesDistance(points: Point[]): Distance {
        let distanceInMeters = 0;

        if (points.length > 1) {
            for (let index = 0; index < points.length - 1; index++) {
                const segmentDistanceInMeters = turfDistance(points[index].coordinates, points[index + 1].coordinates, {
                    units: 'meters',
                });

                distanceInMeters += segmentDistanceInMeters;
            }
        }

        if (distanceInMeters > MeasurementToolService.oneKilometer) {
            const distanceInKilometers = distanceInMeters / MeasurementToolService.oneKilometer;

            return new Distance(DistanceUnit.KILOMETER, distanceInKilometers);
        }

        return new Distance(DistanceUnit.METER, distanceInMeters);
    }

    public async clear(): Promise<void> {
        if (!this.map) {
            return;
        }

        this.removeLayers();
        await this.removePhantomLine();

        if (this.map.getSource(this.measurementSourceId)) {
            this.map.removeSource(this.measurementSourceId);
        }

        this.canDraw.set(true);
        this.resetLabels();

        await this.updateUndoState();
    }

    public async disableMeasurementTool(): Promise<void> {
        if (!this.map) {
            return;
        }

        if (this.addNewPointFunctionInstance) {
            this.map.off('click', this.addNewPointFunctionInstance);
        }

        if (this.drawPhantomLineFunctionInstance) {
            this.map.off('mousemove', this.drawPhantomLineFunctionInstance);
        }

        this.isActive.set(false);
        this.canDraw.set(false);

        await this.clear();
    }

    public enableMeasurementTool(): void {
        if (!this.map) {
            return;
        }

        this.canDraw.set(true);
        this.isActive.set(true);

        if (!this.addNewPointFunctionInstance) {
            this.addNewPointFunctionInstance = this.addNewPoint.bind(this);
        }

        if (!this.drawPhantomLineFunctionInstance) {
            this.drawPhantomLineFunctionInstance = this.drawPhantomLine.bind(this);
        }

        this.map.on('click', this.addNewPointFunctionInstance);
        this.map.on('mousemove', this.drawPhantomLineFunctionInstance);
    }

    public async finishMeasurement(): Promise<void> {
        if (!this.map || !this.canDraw()) {
            return;
        }

        await this.removePhantomLine();
        this.canDraw.set(false);

        await this.updateUndoState();
    }

    public getLayerPaintColorValue(): string {
        return MapComponent.backgroundSource() === TileSourceTypeEnum.OSM ? '#000' : '#fff';
    }

    public removeLayers(layers?: string[]): void {
        if (!this.map) {
            return;
        }

        const layersToRemove = layers ?? this.layerNames;

        for (const layer of layersToRemove) {
            if (this.map.getLayer(layer)) {
                this.map.removeLayer(layer);
            }
        }
    }

    public async removePhantomLine(): Promise<void> {
        if (!this.map) {
            return;
        }

        const phantomSource = this.map.getSource(this.measurementPhantomSourceId) as GeoJSONSource | undefined;
        if (!phantomSource) {
            return;
        }

        const phantomData = (await phantomSource.getData()) as FeatureCollection;
        phantomData.features = [];

        phantomSource.setData(phantomData as GeoJSON.GeoJSON);
    }

    public async undoLastAction(): Promise<void> {
        if (!this.map) {
            return;
        }

        const measurementSource = this.map.getSource(this.measurementSourceId) as GeoJSONSource | undefined;
        if (!measurementSource) {
            return;
        }

        const data = (await measurementSource.getData()) as FeatureCollection;
        const features: Feature[] = data.features;

        if (!features.length) {
            return;
        }

        const geometryTypes: Geometry.TypeEnum[] = [
            Geometry.TypeEnum.LINE_STRING,
            Geometry.TypeEnum.POLYGON,
            Geometry.TypeEnum.POINT,
        ];

        for (const type of geometryTypes) {
            const lastFeature = features[features.length - 1];

            if (!lastFeature) {
                return;
            }

            if (lastFeature.geometry.type === type) {
                features.pop();
            }
        }

        data.features = features;
        measurementSource.setData(data as GeoJSON.GeoJSON);

        await this.removePhantomLine();

        await this.calculateDistance();
        this.canDraw.set(true);
    }

    private addCustomLayer(options: AddLayerObject): void {
        if (!this.map || this.map.getLayer(options.id)) {
            return;
        }

        this.map.addLayer(options);
    }

    private addLineLayer(): void {
        this.addCustomLayer({
            id: 'measurement-lines',
            layout: {},
            paint: {
                'line-color': this.getLayerPaintColorValue(),
                'line-width': 2,
            },
            source: MeasurementToolService.measurementSourceId,
            type: 'line',
        });
    }

    private addMapSource(sourceId: string, features: Feature[] = []): void {
        if (!this.map) {
            return;
        }

        if (this.map.getSource(sourceId)) {
            return;
        }

        this.map.addSource(sourceId, {
            data: {
                features: features,
                type: 'FeatureCollection',
            },
            type: 'geojson',
        } as GeoJSONSourceSpecification);
    }

    private async addNewPoint(mapClickEvent: MapMouseEvent): Promise<void> {
        if (!this.map || !this.canDraw()) {
            return;
        }

        const measurementSource = this.map.getSource(this.measurementSourceId) as GeoJSONSource | undefined;
        const hasPointLayer = !!this.map.getLayer('measurement-points');

        if (measurementSource && hasPointLayer) {
            const dataSource = (await measurementSource.getData()) as FeatureCollection;
            const isPolygonClosed = this.hasClosedPolygon(dataSource, mapClickEvent);

            if (isPolygonClosed) {
                await this.closePolygon(dataSource, measurementSource);

                return;
            }
        }

        const coordinates = [[mapClickEvent.lngLat.lng, mapClickEvent.lngLat.lat]];

        await this.drawFeatureOnSource(coordinates, this.measurementSourceId, Geometry.TypeEnum.POINT);
        await this.drawLineStrings();

        await this.calculateDistance();
        this.addPointLayer();
    }

    private addPhantomLineLayer(): void {
        this.addCustomLayer({
            id: 'measurement-phantom-lines',
            layout: {},
            paint: {
                'line-color': this.getLayerPaintColorValue(),
                'line-dasharray': [2, 2],
                'line-width': 2,
            },
            source: this.measurementPhantomSourceId,
            type: 'line',
        });
    }

    private addPointLayer(): void {
        this.addCustomLayer({
            id: 'measurement-points',
            layout: {},
            paint: {
                'circle-color': this.getLayerPaintColorValue(),
                'circle-radius': 6,
            },
            source: MeasurementToolService.measurementSourceId,
            type: 'circle',
        });
    }

    private addPolygonLayer(): void {
        this.addCustomLayer({
            id: 'measurement-polygon',
            paint: {
                'fill-color': this.getLayerPaintColorValue(),
                'fill-opacity': 0.4,
            },
            source: MeasurementToolService.measurementSourceId,
            type: 'fill',
        });
    }

    private async closePolygon(dataSource: FeatureCollection, measurementSource: GeoJSONSource): Promise<boolean> {
        if (!this.map) {
            return false;
        }

        const features = dataSource.features.filter(
            (feature: Feature) => feature.geometry.type === Geometry.TypeEnum.POINT,
        );

        dataSource.features.push(dataSource.features[0]);

        const points: Point[] = features.map((feature: Feature) => {
            AssertService.assertPointType(feature.geometry);

            return feature.geometry;
        });

        dataSource.features.push({
            geometry: {
                coordinates: [points.map((point) => point.coordinates)],
                type: 'Polygon',
            },
            properties: {},
            type: Feature.TypeEnum.FEATURE,
        });

        measurementSource.setData(dataSource as GeoJSON.GeoJSON);

        this.addPolygonLayer();
        await this.removePhantomLine();
        await this.drawLineStrings();
        await this.calculateDistance(true);

        this.canDraw.set(false);

        return true;
    }

    private async drawFeatureOnSource(
        coordinates: number[][],
        sourceId: string,
        type: Geometry.TypeEnum,
    ): Promise<void> {
        if (!this.map) {
            return;
        }

        if (!(this.map.getSource(sourceId) as GeoJSONSource)) {
            this.mapSourceService.addCustomSource(sourceId);
        }

        const newFeature = {
            geometry: {
                coordinates: type === Geometry.TypeEnum.POINT ? coordinates[0] : coordinates,
                type: type,
            },
            properties: {},
            type: Feature.TypeEnum.FEATURE,
        };

        const source = this.map.getSource(sourceId) as GeoJSONSource;
        const dataSource = (await source.getData()) as FeatureCollection;
        dataSource.features.push(newFeature as Feature);
        source.setData(dataSource as GeoJSON.GeoJSON);
    }

    private async drawLineStrings(): Promise<void> {
        if (!this.map) {
            return;
        }

        const source = this.map.getSource(this.measurementSourceId) as GeoJSONSource | undefined;
        if (!source) {
            return;
        }

        const sourceData = (await source.getData()) as FeatureCollection;
        if (!sourceData) {
            return;
        }

        const points: Point[] = sourceData.features
            .filter((feature: Feature) => feature.geometry.type === Point.TypeEnum.POINT)
            .map((feature: Feature) => {
                AssertService.assertPointType(feature.geometry);

                return feature.geometry;
            });

        if (points.length < 2) {
            return;
        }

        const lastTwoPoints = points.slice(-2);
        const coordinates = lastTwoPoints.map((point) => point.coordinates);

        this.addLineLayer();
        await this.drawFeatureOnSource(
            coordinates,
            MeasurementToolService.measurementSourceId,
            Geometry.TypeEnum.LINE_STRING,
        );
    }

    private async drawPhantomLine(mapMouseEvent: MapMouseEvent): Promise<void> {
        if (!this.map || !this.canDraw()) {
            return;
        }

        const measurementSource = this.map.getSource(this.measurementSourceId) as GeoJSONSource | undefined;
        if (!measurementSource) {
            return;
        }

        const measurementDataSource = (await measurementSource.getData()) as FeatureCollection;

        const points = measurementDataSource.features.filter(
            (feature: Feature) => feature.geometry.type === Point.TypeEnum.POINT,
        );

        if (!points.length) {
            return;
        }

        const lastPoint = points[points.length - 1].geometry;
        AssertService.assertPointType(lastPoint);

        const currentMousePosition = mapMouseEvent.lngLat;

        const lineString: Feature = {
            geometry: {
                coordinates: [
                    [lastPoint.coordinates[0], lastPoint.coordinates[1]],
                    [currentMousePosition.lng, currentMousePosition.lat],
                ],
                type: 'LineString',
            },
            properties: {
                id: 'phantom-line',
            },
            type: Feature.TypeEnum.FEATURE,
        };

        this.addMapSource(this.measurementPhantomSourceId);

        const phantomSource = this.map.getSource(this.measurementPhantomSourceId) as GeoJSONSource;
        const phantomDataSource = (await phantomSource.getData()) as FeatureCollection;

        phantomDataSource.features = [lineString];
        phantomSource.setData(phantomDataSource as GeoJSON);

        this.addPhantomLineLayer();
    }

    private hasClosedPolygon(dataSource: FeatureCollection, mapClickEvent: MapMouseEvent): boolean {
        if (!this.map) {
            return false;
        }

        const sensibility = 10;

        const bbox: [number[], number[]] = [
            [mapClickEvent.point.x - sensibility, mapClickEvent.point.y - sensibility],
            [mapClickEvent.point.x + sensibility, mapClickEvent.point.y + sensibility],
        ];

        const points: Point[] = dataSource.features
            .filter((feature: Feature) => feature.geometry.type === Geometry.TypeEnum.POINT)
            .map((feature: Feature) => {
                AssertService.assertPointType(feature.geometry);

                return feature.geometry;
            });

        if (points.length < 3) {
            return false;
        }

        const firstPointOnMap = this.map.project(points[0].coordinates as LngLatLike);

        return (
            firstPointOnMap.x >= bbox[0][0] &&
            firstPointOnMap.x <= bbox[1][0] &&
            firstPointOnMap.y >= bbox[0][1] &&
            firstPointOnMap.y <= bbox[1][1]
        );
    }

    private resetLabels(): void {
        this.distance.set(new Distance(DistanceUnit.METER, 0));
        this.area.set(new Area(AreaUnit.SQUARE_METER, 0));
        this.hasDistance.set(false);
        this.hasArea.set(false);
    }

    private async updateUndoState(): Promise<void> {
        if (!this.map) {
            this.canUndo.set(false);

            return;
        }

        const measurementSource = this.map.getSource(this.measurementSourceId) as GeoJSONSource | undefined;
        if (!measurementSource) {
            this.canUndo.set(false);

            return;
        }

        const data = (await measurementSource.getData()) as FeatureCollection;
        this.canUndo.set((data?.features?.length ?? 0) > 0);
    }
}
